import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Plus, Pencil, Search, Upload, Download, FileSpreadsheet, FileText, X, Filter, ArrowUp, ArrowDown, ArrowUpDown, GitMerge, Trash2, MapPinned, Link2 } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Spinner, StatusBadge, EmptyState } from "../../components/common";
import { MergeMasterDialog, DuplicateMastersBanner, MERGEABLE_MASTERS } from "../../components/MergeMasterDialog";
import { DeleteMasterDialog } from "../../components/DeleteMasterDialog";
import { BulkDeleteMastersDialog } from "../../components/BulkDeleteMastersDialog";
import { MohallaBackfillDialog } from "../../components/MohallaBackfillDialog";
import { AreaLinkageDialog } from "../../components/AreaLinkageDialog";
import { Checkbox } from "../../components/ui/checkbox";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { api, errMsg, downloadBlob } from "../../lib/api";
import { useRoleModules } from "../../lib/roleModules";
import { StaffRolesSection } from "./StaffRoles";
import { MergeHistoryTab } from "../../components/MergeHistoryTab";
import { ReadOnlyNotice } from "../../components/FeatureGate";

// Phase 152: master table key ("sub-categories") -> feature key ("sub_categories")
const featKey = (k) => k.replace(/-/g, "_");

const FIELD_LABELS = {
  zone_code: "Zone Code", description: "Description", ward_number: "Ward Number", pincode: "Pincode",
  width_in_feet: "Width (e.g. 30, 30A, 12B ft)", start_date: "Start Date", end_date: "End Date", is_current: "Current FY",
  floor_number: "Floor Number", tax_code: "Tax Code", is_recurring: "Recurring", mode_code: "Mode Code",
  is_online: "Online Mode", rate_per_sqft: "Rate / sqft", flat_rate: "Flat Rate", percentage_rate: "Percentage Rate",
  penalty_rate_monthly: "Penalty % / month", rebate_percentage: "Rebate %", min_amount: "Min Amount",
  max_amount: "Max Amount", effective_from: "Effective From", effective_to: "Effective To",
  tax_head_id: "Tax Head", financial_year_id: "Financial Year", category_id: "Category",
  sub_category_id: "Sub Category", usage_type_id: "Usage Type", zone_id: "Zone", construction_type_id: "Construction Type",
  ward_id: "Ward",
};
const plural = (s) => (!s ? "" : /y$/i.test(s) ? s.replace(/y$/i, "ies") : `${s}s`);
const singular = (s) => (!s ? "" : /ies$/i.test(s) ? s.replace(/ies$/i, "y") : s.replace(/s$/i, ""));
const BOOL_FIELDS = ["is_current", "is_recurring", "is_online"];
const DATE_FIELDS = ["start_date", "end_date", "effective_from", "effective_to"];
const NUM_FIELDS = ["ward_number", "floor_number", "rate_per_sqft", "flat_rate", "percentage_rate",
  "penalty_rate_monthly", "rebate_percentage", "min_amount", "max_amount"];
const REF_FIELDS = { tax_head_id: "tax-heads", financial_year_id: "financial-years", category_id: "categories",
  sub_category_id: "sub-categories", usage_type_id: "usage-types", zone_id: "zones", construction_type_id: "construction-types" };

export default function Masters() {
  const { code } = useParams();
  const [meta, setMeta] = useState(null);
  const [activeKey, setActiveKey] = useState("zones");
  const [items, setItems] = useState(null);
  const [search, setSearch] = useState("");
  const [parents, setParents] = useState([]);
  const [refOptions, setRefOptions] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [importBusy, setImportBusy] = useState(false);
  const [zoneList, setZoneList] = useState([]);
  const [zoneFilter, setZoneFilter] = useState("");
  // Table filters (search + parent / zone / status) and export state
  const [parentFilter, setParentFilter] = useState("");   // wards: zone | mohallas: ward | sub-categories: category …
  const [zoneTableFilter, setZoneTableFilter] = useState(""); // mohallas only: zone → narrows the ward list + rows
  const [statusFilter, setStatusFilter] = useState("all");
  const [exporting, setExporting] = useState(false);
  const [sort, setSort] = useState({ key: null, dir: "asc" });   // column sorting
  // Phase 116: duplicate detection + merge (zones / wards / mohallas)
  const [dupGroups, setDupGroups] = useState([]);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeInitial, setMergeInitial] = useState(null);
  // Phase 147: delete with linked-record check (permanent only when nothing active is linked, else deactivate)
  const [deleteTarget, setDeleteTarget] = useState(null);
  // Phase 148: checkbox multi-select + Delete Selected
  const [selected, setSelected] = useState(() => new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkIds, setBulkIds] = useState([]);
  // Phase 156: Mohallas → fill the mohalla on properties that have none (per ward, undoable)
  const [backfillOpen, setBackfillOpen] = useState(false);
  const [linkageOpen, setLinkageOpen] = useState(false);   // Phase 160: area linkage check + repair

  // Phase 152: per-master-type access — hidden when "No access", read-only when "View only"
  const access = useRoleModules(code);
  const visibleMeta = useMemo(() => (meta || []).filter((m) => access.featureAllowed("masters", featKey(m.key))), [meta, access]);
  const staffRolesAllowed = access.featureAllowed("masters", "staff_roles");
  const mergeHistoryAllowed = access.featureAllowed("masters", "merge_history");   // Phase 164
  const SPECIAL_KEYS = { staff_roles: staffRolesAllowed, merge_history: mergeHistoryAllowed };
  const canWrite = activeKey === "staff_roles" ? access.canWrite("masters", "staff_roles") : access.canWrite("masters", featKey(activeKey));
  useEffect(() => {
    if (!meta || !access.loaded) return;
    const ok = activeKey in SPECIAL_KEYS ? SPECIAL_KEYS[activeKey] : visibleMeta.some((m) => m.key === activeKey);
    if (!ok) {
      const first = visibleMeta[0]?.key || (staffRolesAllowed ? "staff_roles" : mergeHistoryAllowed ? "merge_history" : null);
      if (first) setActiveKey(first);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta, access.loaded, visibleMeta, staffRolesAllowed, mergeHistoryAllowed, activeKey]);

  const cfg = meta?.find((m) => m.key === activeKey);
  const resetFilters = () => { setSearch(""); setParentFilter(""); setZoneTableFilter(""); setStatusFilter("all"); setSort({ key: null, dir: "asc" }); };
  const mergeable = !!MERGEABLE_MASTERS[activeKey];

  const loadMeta = useCallback(() => api.get(`/t/${code}/masters-meta`).then((r) => setMeta(r.data)), [code]);
  const loadItems = useCallback(async (key) => {
    setItems(null);
    const r = await api.get(`/t/${code}/masters/${key}`);
    setItems(r.data);
  }, [code]);
  const loadDuplicates = useCallback(async (key) => {
    if (!MERGEABLE_MASTERS[key]) { setDupGroups([]); return; }
    try {
      const r = await api.get(`/t/${code}/masters/${key}/duplicates`);
      setDupGroups(r.data?.groups || []);
    } catch { setDupGroups([]); }
  }, [code]);

  useEffect(() => { loadMeta().catch(() => {}); }, [loadMeta]);
  useEffect(() => {
    setSelected(new Set());                       // selection never survives a table switch
    if (activeKey !== "staff_roles" && activeKey !== "merge_history") { loadItems(activeKey).catch(() => {}); loadDuplicates(activeKey); }
  }, [activeKey, loadItems, loadDuplicates]);
  const openMerge = (initial = null) => { setMergeInitial(initial); setMergeOpen(true); };
  const afterMerge = () => { loadItems(activeKey); loadDuplicates(activeKey); loadMeta(); };
  // drop ids that disappeared (deleted / merged) so the selection count always matches visible rows
  useEffect(() => {
    if (!items) return;
    setSelected((prev) => {
      const live = new Set(items.map((it) => it.id));
      const next = new Set([...prev].filter((id) => live.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [items]);
  const toggleSelect = (id, on) => setSelected((prev) => { const n = new Set(prev); if (on) n.add(id); else n.delete(id); return n; });
  const openBulkDelete = () => { setBulkIds([...selected]); setBulkOpen(true); };
  const afterBulk = () => { setSelected(new Set()); loadItems(activeKey); loadDuplicates(activeKey); loadMeta(); };

  useEffect(() => {
    const c = meta?.find((m) => m.key === activeKey);
    if (c?.parent) {
      const parentKey = meta.find((m) => m.name_field === c.parent.label_field)?.key ||
        Object.entries({ zones: "zone_name", wards: "ward_name", categories: "category_name", "usage-types": "usage_type_name" })
          .find(([, nf]) => nf === c.parent.label_field)?.[0];
      if (parentKey) api.get(`/t/${code}/masters/${parentKey}`).then((r) => setParents(r.data)).catch(() => {});
      // zone list for cascading ward selection (mohallas: pick zone -> only its wards)
      if (c.parent.field === "ward_id") api.get(`/t/${code}/masters/zones`).then((r) => setZoneList(r.data)).catch(() => {});
    } else setParents([]);
    if (activeKey === "tax-rates" && meta) {
      Promise.all(Object.values(REF_FIELDS).map((k) => api.get(`/t/${code}/masters/${k}`).then((r) => [k, r.data])))
        .then((pairs) => setRefOptions(Object.fromEntries(pairs))).catch(() => {});
    }
  }, [activeKey, meta, code]);

  const openCreate = () => {
    setEditing(null);
    setForm({});
    setZoneFilter("");
    setDialogOpen(true);
  };
  const openEdit = (item) => {
    setEditing(item);
    setForm({ ...item });
    if (cfg?.parent?.field === "ward_id") {
      setZoneFilter(parents.find((p) => p.id === item.ward_id)?.zone_id || "");
    }
    setDialogOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (cfg?.hide_name && cfg?.name_field) delete payload[cfg.name_field]; // backend derives it
      if (editing) await api.put(`/t/${code}/masters/${activeKey}/${editing.id}`, payload);
      else await api.post(`/t/${code}/masters/${activeKey}`, payload);
      toast.success(editing ? "Updated" : "Created");
      setDialogOpen(false);
      loadItems(activeKey);
      loadMeta();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const toggle = async (item) => {
    await api.put(`/t/${code}/masters/${activeKey}/${item.id}/toggle`);
    loadItems(activeKey);
  };

  const importSupported = cfg?.name_field && activeKey !== "tax-rates";

  const downloadTemplate = async () => {
    try {
      await downloadBlob(`/t/${code}/masters/${activeKey}/import-template`, `${activeKey}-import-template.xlsx`);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const runImport = async () => {
    if (!importFile) return toast.error("Choose an XLSX or CSV file first");
    setImportBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      const r = await api.post(`/t/${code}/masters/${activeKey}/import`, fd);
      setImportResult(r.data);
      toast.success(`${r.data.created} record(s) imported into ${r.data.master}`);
      loadItems(activeKey);
      loadMeta();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setImportBusy(false);
    }
  };

  const refLabel = (refKey, id) => {
    const opts = refOptions[refKey] || [];
    const found = opts.find((o) => o.id === id);
    if (!found) return "-";
    return found.tax_head_name || found.fy_label || found.category_name || found.sub_category_name || found.usage_type_name || found.zone_name || found.const_type_name || "-";
  };

  // ---- Table filters -------------------------------------------------------
  // Mohallas: ward → zone lookup so we can filter/search/export by zone as well
  const wardZone = useMemo(() => Object.fromEntries((cfg?.parent?.field === "ward_id" ? parents : []).map((w) => [w.id, w.zone_id])), [cfg, parents]);
  const zoneName = useMemo(() => Object.fromEntries(zoneList.map((z) => [z.id, z.zone_name])), [zoneList]);
  const parentOptions = cfg?.parent?.field === "ward_id" && zoneTableFilter ? parents.filter((p) => p.zone_id === zoneTableFilter) : parents;

  const cellText = (it, f) => REF_FIELDS[f] ? refLabel(REF_FIELDS[f], it[f]) : BOOL_FIELDS.includes(f) ? (it[f] ? "Yes" : "No") : String(it[f] ?? "");

  const filtered = useMemo(() => (items || []).filter((it) => {
    if (statusFilter === "active" && !it.is_active) return false;
    if (statusFilter === "inactive" && it.is_active) return false;
    if (parentFilter && cfg?.parent && it[cfg.parent.field] !== parentFilter) return false;
    if (zoneTableFilter && cfg?.parent?.field === "ward_id" && wardZone[it.ward_id] !== zoneTableFilter) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    const hay = [cfg?.name_field ? it[cfg.name_field] : null, it.parent_label,
      cfg?.parent?.field === "ward_id" ? zoneName[wardZone[it.ward_id]] : null,
      ...(cfg?.fields || []).map((f) => cellText(it, f))];
    return hay.some((h) => h && String(h).toLowerCase().includes(q));
  }), [items, statusFilter, parentFilter, zoneTableFilter, search, cfg, wardZone, zoneName, refOptions]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasFilters = !!(search || parentFilter || zoneTableFilter || statusFilter !== "all");
  const parentFilterLabel = cfg?.parent ? (FIELD_LABELS[cfg.parent.field] || "Parent") : "";

  // ---- Column sorting (click a header: ascending → descending → default order) ----
  const sortValue = (it, key) => {
    if (key === "name") return it[cfg?.name_field] ?? "";
    if (key === "zone") return zoneName[wardZone[it.ward_id]] ?? "";
    if (key === "parent") return it.parent_label ?? "";
    if (key === "status") return it.is_active ? 0 : 1;           // Active first when ascending
    if (NUM_FIELDS.includes(key)) return it[key] === "" || it[key] == null ? null : Number(it[key]);
    if (BOOL_FIELDS.includes(key)) return it[key] ? 0 : 1;        // Yes first when ascending
    return cellText(it, key);
  };
  const isEmpty = (v) => v == null || v === "" || (typeof v === "number" && Number.isNaN(v));
  const sorted = useMemo(() => {
    if (!sort.key) return filtered;
    const dir = sort.dir === "desc" ? -1 : 1;
    const rows = filtered.map((it, i) => ({ it, i, v: sortValue(it, sort.key) }));
    rows.sort((a, b) => {
      const ae = isEmpty(a.v), be = isEmpty(b.v);
      if (ae || be) return ae && be ? a.i - b.i : ae ? 1 : -1;   // blanks always last
      const c = typeof a.v === "number" && typeof b.v === "number"
        ? a.v - b.v
        : String(a.v).localeCompare(String(b.v), undefined, { numeric: true, sensitivity: "base" });
      return c !== 0 ? c * dir : a.i - b.i;                        // stable
    });
    return rows.map((r) => r.it);
  }, [filtered, sort]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggleSort = (key) => setSort((s) => (s.key !== key ? { key, dir: "asc" } : s.dir === "asc" ? { key, dir: "desc" } : { key: null, dir: "asc" }));

  const SortableHead = ({ colKey, label, className = "" }) => {
    const active = sort.key === colKey;
    const Icon = active ? (sort.dir === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;
    return (
      <TableHead className={className} aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}>
        <button type="button" onClick={() => toggleSort(colKey)} data-testid={`master-sort-${colKey}`} data-sort-dir={active ? sort.dir : "none"}
          className={`inline-flex items-center gap-1 -mx-1 px-1 py-0.5 rounded hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${active ? "text-foreground font-semibold" : ""}`}
          title={active ? (sort.dir === "asc" ? "Sorted ascending — click for descending" : "Sorted descending — click to reset") : `Sort by ${label}`}>
          {label}
          <Icon className={`h-3.5 w-3.5 ${active ? "text-primary" : "text-muted-foreground/60"}`} />
        </button>
      </TableHead>
    );
  };

  const exportTable = async (format, scope) => {
    setExporting(true);
    try {
      const qs = new URLSearchParams({ format });
      if (scope === "filtered") {
        if (search) qs.set("search", search);
        if (parentFilter) qs.set("parent_id", parentFilter);
        if (zoneTableFilter) qs.set("zone_id", zoneTableFilter);
        if (statusFilter !== "all") qs.set("status", statusFilter);
      }
      if (sort.key) { qs.set("sort", sort.key); qs.set("dir", sort.dir); }   // export follows the on-screen order
      const stamp = new Date().toISOString().slice(0, 10);
      await downloadBlob(`/t/${code}/masters/${activeKey}/export?${qs.toString()}`, `${activeKey}_${code}_${stamp}.${format}`);
      toast.success(`${cfg?.label} exported (${format === "csv" ? "CSV" : "Excel"}, ${scope === "filtered" ? filtered.length : (items || []).length} rows)`);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setExporting(false);
    }
  };

  if (!meta) return <Spinner />;

  return (
    <div>
      <PageHeader title="Master Data" subtitle="Manage all 15 master tables for this municipality" />
      <div className="flex gap-4 flex-col lg:flex-row">
        <Card className="shadow-sm lg:w-60 shrink-0">
          <ScrollArea className="lg:h-[calc(100vh-220px)]">
            <div className="p-2 flex lg:flex-col gap-1 overflow-x-auto">
              {visibleMeta.map((m) => (
                <button key={m.key} onClick={() => { setActiveKey(m.key); resetFilters(); }}
                  data-testid={`master-tab-${m.key}`}
                  className={`text-left px-3 py-2 rounded-lg text-sm whitespace-nowrap flex items-center justify-between gap-2 ${
                    activeKey === m.key ? "bg-primary text-primary-foreground" : "hover:bg-muted"
                  }`}>
                  <span>{m.label}</span>
                  <span className={`text-[11px] tabular-nums px-1.5 rounded ${activeKey === m.key ? "bg-primary-foreground/20" : "bg-muted-foreground/10"}`}>{m.count}</span>
                </button>
              ))}
              {/* Phase 71: staff role management lives alongside the master tables */}
              {staffRolesAllowed && <button onClick={() => { setActiveKey("staff_roles"); resetFilters(); }}
                data-testid="master-tab-staff_roles"
                className={`text-left px-3 py-2 rounded-lg text-sm whitespace-nowrap flex items-center justify-between gap-2 ${
                  activeKey === "staff_roles" ? "bg-primary text-primary-foreground" : "hover:bg-muted"
                }`}>
                <span>Staff Roles</span>
              </button>}
              {/* Phase 164: log of every zone / ward / mohalla merge */}
              {mergeHistoryAllowed && <button onClick={() => { setActiveKey("merge_history"); resetFilters(); }}
                data-testid="master-tab-merge_history"
                className={`text-left px-3 py-2 rounded-lg text-sm whitespace-nowrap flex items-center justify-between gap-2 ${
                  activeKey === "merge_history" ? "bg-primary text-primary-foreground" : "hover:bg-muted"
                }`}>
                <span className="flex items-center gap-1.5"><GitMerge className="h-3.5 w-3.5" aria-hidden="true" /> Merge History</span>
              </button>}
              {visibleMeta.length === 0 && !staffRolesAllowed && !mergeHistoryAllowed && access.loaded && (
                <p className="text-xs text-muted-foreground px-3 py-2" data-testid="masters-none-allowed">No master tables are available for your access level.</p>
              )}
            </div>
          </ScrollArea>
        </Card>

        <Card className="shadow-sm flex-1 min-w-0">
          <CardContent className="p-4">
            {activeKey === "staff_roles" ? (
              <StaffRolesSection code={code} readOnly={!canWrite} />
            ) : activeKey === "merge_history" ? (
              <MergeHistoryTab code={code} />
            ) : (
            <>
            <div className="flex flex-col gap-2 mb-4" data-testid="master-toolbar">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap flex-1 min-w-0">
                  <div className="relative flex-1 min-w-[200px] max-w-sm">
                    <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input className="pl-9" placeholder={`Search ${cfg?.label?.toLowerCase()}...`} value={search}
                      onChange={(e) => setSearch(e.target.value)} data-testid="master-search-input" aria-label={`Search ${cfg?.label}`} />
                  </div>
                  {/* Mohallas: Zone → Ward cascade */}
                  {cfg?.parent?.field === "ward_id" && (
                    <Select value={zoneTableFilter || "all"} onValueChange={(v) => { setZoneTableFilter(v === "all" ? "" : v); setParentFilter(""); }}>
                      <SelectTrigger className="w-[160px]" data-testid="master-filter-zone" aria-label="Filter by zone"><SelectValue placeholder="All zones" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All zones</SelectItem>
                        {zoneList.map((z) => <SelectItem key={z.id} value={z.id}>{z.zone_name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                  {cfg?.parent && (
                    <Select value={parentFilter || "all"} onValueChange={(v) => setParentFilter(v === "all" ? "" : v)}>
                      <SelectTrigger className="w-[180px]" data-testid="master-filter-parent" aria-label={`Filter by ${parentFilterLabel}`}>
                        <SelectValue placeholder={`All ${plural(parentFilterLabel).toLowerCase()}`} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All {plural(parentFilterLabel).toLowerCase()}</SelectItem>
                        {parentOptions.map((p) => <SelectItem key={p.id} value={p.id}>{p[cfg.parent.label_field]}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-[130px]" data-testid="master-filter-status" aria-label="Filter by status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All status</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                  {hasFilters && (
                    <Button variant="ghost" size="sm" onClick={resetFilters} data-testid="master-clear-filters">
                      <X className="h-3.5 w-3.5 mr-1" /> Clear
                    </Button>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" disabled={exporting || !items || items.length === 0} data-testid="master-export-button">
                        <Download className="h-4 w-4 mr-2" /> {exporting ? "Preparing…" : "Export"}
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-60" data-testid="master-export-menu">
                      <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
                        {hasFilters ? `Filtered rows (${filtered.length})` : `All rows (${(items || []).length})`}
                      </DropdownMenuLabel>
                      <DropdownMenuItem onClick={() => exportTable("xlsx", "filtered")} data-testid="master-export-xlsx-filtered">
                        <FileSpreadsheet className="h-4 w-4 mr-2" /> Excel (.xlsx)
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => exportTable("csv", "filtered")} data-testid="master-export-csv-filtered">
                        <FileText className="h-4 w-4 mr-2" /> CSV (.csv)
                      </DropdownMenuItem>
                      {hasFilters && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">All rows ({(items || []).length}) — ignore filters</DropdownMenuLabel>
                          <DropdownMenuItem onClick={() => exportTable("xlsx", "all")} data-testid="master-export-xlsx-all">
                            <FileSpreadsheet className="h-4 w-4 mr-2" /> Excel (.xlsx)
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => exportTable("csv", "all")} data-testid="master-export-csv-all">
                            <FileText className="h-4 w-4 mr-2" /> CSV (.csv)
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                  {importSupported && canWrite && (
                    <Button variant="outline" onClick={() => { setImportResult(null); setImportFile(null); setImportOpen(true); }} data-testid="master-import-button">
                      <Upload className="h-4 w-4 mr-2" /> Import
                    </Button>
                  )}
                  {mergeable && canWrite && (
                    <Button variant="outline" onClick={() => openMerge(null)} disabled={!items || items.length < 2} data-testid="master-merge-button">
                      <GitMerge className="h-4 w-4 mr-2" /> Merge duplicates
                    </Button>
                  )}
                  {activeKey === "mohallas" && canWrite && (
                    <Button variant="outline" onClick={() => setBackfillOpen(true)} data-testid="master-mohalla-backfill-button">
                      <MapPinned className="h-4 w-4 mr-2" /> Mohalla Backfill
                    </Button>
                  )}
                  {mergeable && canWrite && (
                    <Button variant="outline" onClick={() => setLinkageOpen(true)} title="Find properties linked to a ward of another zone or a mohalla of another ward, and re-link them"
                      data-testid="master-area-linkage-button">
                      <Link2 className="h-4 w-4 mr-2" /> Check area links
                    </Button>
                  )}
                  {canWrite && <Button onClick={openCreate} data-testid="master-add-button"><Plus className="h-4 w-4 mr-2" /> Add {singular(cfg?.label)}</Button>}
                </div>
              </div>
              {!canWrite && <ReadOnlyNotice what={cfg?.label} testId="masters-readonly-notice" />}
              {mergeable && canWrite && <DuplicateMastersBanner groups={dupGroups} singular={MERGEABLE_MASTERS[activeKey]} onMerge={openMerge} />}
              {items && (
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-xs text-muted-foreground flex items-center gap-1.5" data-testid="master-showing-count">
                    {hasFilters && <Filter className="h-3 w-3" />}
                    Showing <span className="font-medium text-foreground tabular-nums">{filtered.length}</span> of{" "}
                    <span className="font-medium text-foreground tabular-nums">{items.length}</span> {cfg?.label?.toLowerCase()}
                    {hasFilters ? " matching your filters" : ""}
                  </p>
                  {selected.size > 0 && canWrite && (
                    <div className="flex items-center gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-2.5 py-1.5" data-testid="master-selection-bar" role="status">
                      <span className="text-sm font-medium tabular-nums" data-testid="master-selected-count">{selected.size} selected</span>
                      <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setSelected(new Set())} data-testid="master-clear-selection">
                        <X className="h-3.5 w-3.5 mr-1" /> Clear
                      </Button>
                      <Button variant="destructive" size="sm" className="h-7" onClick={openBulkDelete} data-testid="master-delete-selected">
                        <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete Selected
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
            {!items ? <Spinner /> : filtered.length === 0 ? (
              hasFilters
                ? <EmptyState title={`No ${cfg?.label?.toLowerCase()} match your filters`} subtitle="Try a different search term or clear the filters"
                    action={<Button variant="outline" size="sm" onClick={resetFilters} data-testid="master-empty-clear-filters"><X className="h-3.5 w-3.5 mr-1" /> Clear filters</Button>} />
                : <EmptyState title={`No ${cfg?.label?.toLowerCase()} found`} subtitle="Add your first entry to get started" />
            ) : (
              <div className="overflow-x-auto">
                <Table data-testid="master-table">
                  <TableHeader>
                    <TableRow>
                      {canWrite && <TableHead className="w-10 pr-0">
                        <Checkbox
                          checked={sorted.length > 0 && sorted.every((it) => selected.has(it.id)) ? true : sorted.some((it) => selected.has(it.id)) ? "indeterminate" : false}
                          onCheckedChange={(on) => setSelected((prev) => {
                            const n = new Set(prev);
                            sorted.forEach((it) => { if (on === true) n.add(it.id); else n.delete(it.id); });
                            return n;
                          })}
                          aria-label={`Select all ${filtered.length} ${cfg?.label?.toLowerCase() || "rows"} shown`} data-testid="master-select-all" />
                      </TableHead>}
                      {cfg?.name_field && !cfg?.hide_name && <SortableHead colKey="name" label="Name" />}
                      {cfg?.parent?.field === "ward_id" && <SortableHead colKey="zone" label="Zone" />}
                      {cfg?.parent && <SortableHead colKey="parent" label={FIELD_LABELS[cfg.parent.field] || "Parent"} />}
                      {cfg?.fields.slice(0, 4).map((f) => <SortableHead key={f} colKey={f} label={FIELD_LABELS[f] || f} />)}
                      <SortableHead colKey="status" label="Status" />
                      {canWrite && <TableHead className="text-right">Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sorted.map((it) => (
                      <TableRow key={it.id} className={`hover:bg-muted/60 ${selected.has(it.id) ? "bg-primary/5" : ""}`} data-state={selected.has(it.id) ? "selected" : undefined}>
                        {canWrite && <TableCell className="w-10 pr-0">
                          <Checkbox checked={selected.has(it.id)} onCheckedChange={(on) => toggleSelect(it.id, on === true)}
                            aria-label={`Select ${(cfg?.name_field && it[cfg.name_field]) || it.id}`} data-testid={`master-select-${it.id}`} />
                        </TableCell>}
                        {cfg?.name_field && !cfg?.hide_name && <TableCell className="font-medium">{it[cfg.name_field]}</TableCell>}
                        {cfg?.parent?.field === "ward_id" && <TableCell className="text-muted-foreground">{zoneName[wardZone[it.ward_id]] || "-"}</TableCell>}
                        {cfg?.parent && <TableCell>{it.parent_label || "-"}</TableCell>}
                        {cfg?.fields.slice(0, 4).map((f) => (
                          <TableCell key={f}>
                            {REF_FIELDS[f] ? refLabel(REF_FIELDS[f], it[f])
                              : BOOL_FIELDS.includes(f) ? (it[f] ? "Yes" : "No")
                              : (it[f] ?? "-") === "" ? "-" : String(it[f] ?? "-")}
                          </TableCell>
                        ))}
                        <TableCell><StatusBadge status={it.is_active ? "active" : "inactive"} /></TableCell>
                        {canWrite && <TableCell className="text-right">
                          <div className="flex justify-end items-center gap-2">
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(it)} data-testid={`master-edit-${it.id}`}>
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Switch checked={!!it.is_active} onCheckedChange={() => toggle(it)} data-testid={`master-toggle-${it.id}`}
                              aria-label={it.is_active ? "Deactivate" : "Activate"} />
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                              onClick={() => setDeleteTarget(it)} data-testid={`master-delete-${it.id}`} aria-label={`Delete ${singular(cfg?.label)}`} title="Delete">
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </TableCell>}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
            </>
            )}
          </CardContent>
        </Card>
      </div>

      {mergeable && items && (
        <MergeMasterDialog open={mergeOpen} onOpenChange={setMergeOpen} code={code} masterType={activeKey}
          items={items} nameField={cfg?.name_field} parentField={cfg?.parent?.field}
          parentLabelOf={(pid) => parents.find((p) => p.id === pid)?.[cfg?.parent?.label_field]}
          initial={mergeInitial} onMerged={afterMerge} />
      )}

      <DeleteMasterDialog open={!!deleteTarget} onOpenChange={(v) => { if (!v) setDeleteTarget(null); }} code={code}
        masterType={activeKey} item={deleteTarget} nameField={cfg?.name_field}
        onDeleted={() => { loadItems(activeKey); loadDuplicates(activeKey); loadMeta(); }}
        onDeactivated={() => loadItems(activeKey)}
        onMerge={mergeable ? (it) => openMerge({ sourceId: it.id }) : undefined} />

      <BulkDeleteMastersDialog open={bulkOpen} onOpenChange={setBulkOpen} code={code} masterType={activeKey} ids={bulkIds}
        nameOf={(id) => { const it = (items || []).find((x) => x.id === id); return it && cfg?.name_field ? it[cfg.name_field] : undefined; }}
        onDone={afterBulk} />

      {activeKey === "mohallas" && canWrite && (
        <MohallaBackfillDialog code={code} open={backfillOpen} onOpenChange={setBackfillOpen} onDone={loadMeta} />
      )}
      {mergeable && canWrite && (
        <AreaLinkageDialog code={code} open={linkageOpen} onOpenChange={setLinkageOpen} onDone={() => { loadMeta(); loadItems(activeKey); }} />
      )}

      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Bulk import — {cfg?.label}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Upload an XLSX or CSV file. Duplicates are skipped automatically and missing parent records
              (e.g. zones for wards) are created by name.
            </p>
            <Button variant="outline" className="w-full" onClick={downloadTemplate} data-testid="master-import-template-button">
              <Download className="h-4 w-4 mr-2" /> Download template (.xlsx)
            </Button>
            <div className="space-y-1">
              <Label>File</Label>
              <Input type="file" accept=".xlsx,.xls,.csv" onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                data-testid="master-import-file-input" />
            </div>
            <Button className="w-full" onClick={runImport} disabled={importBusy || !importFile} data-testid="master-import-submit-button">
              <FileSpreadsheet className="h-4 w-4 mr-2" /> {importBusy ? "Importing..." : "Import data"}
            </Button>
            {importResult && (
              <div className="border rounded-lg p-3 text-sm space-y-1.5" data-testid="master-import-result">
                <div className="flex justify-between"><span className="text-muted-foreground">Rows in file</span><span className="font-medium">{importResult.total_rows}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Created</span><span className="font-medium text-emerald-700">{importResult.created}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Skipped (duplicates)</span><span className="font-medium">{importResult.skipped_duplicates}</span></div>
                {importResult.parents_auto_created > 0 && (
                  <div className="flex justify-between"><span className="text-muted-foreground">Parents auto-created</span><span className="font-medium">{importResult.parents_auto_created}</span></div>
                )}
                {importResult.error_count > 0 && (
                  <div>
                    <p className="text-destructive font-medium">{importResult.error_count} error(s):</p>
                    <div className="max-h-28 overflow-y-auto mt-1 space-y-0.5">
                      {importResult.errors.map((e, i) => (
                        <p key={i} className="text-xs text-muted-foreground">Row {e.row}: {e.error}</p>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Edit" : "Add"} {singular(cfg?.label)}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-3">
            {cfg?.name_field && !cfg?.hide_name && (
              <div className="space-y-1">
                <Label>Name</Label>
                <Input value={form[cfg.name_field] || ""} onChange={(e) => setForm({ ...form, [cfg.name_field]: e.target.value })}
                  required data-testid="master-form-name-input" />
              </div>
            )}
            {cfg?.parent && (
              <>
                {cfg.parent.field === "ward_id" && (
                  <div className="space-y-1">
                    <Label>Zone (filter wards)</Label>
                    <Select value={zoneFilter || "all"} onValueChange={(v) => { setZoneFilter(v === "all" ? "" : v); setForm({ ...form, ward_id: "" }); }}>
                      <SelectTrigger data-testid="master-form-zone-filter"><SelectValue placeholder="All zones" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All zones</SelectItem>
                        {zoneList.map((z) => <SelectItem key={z.id} value={z.id}>{z.zone_name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="space-y-1">
                  <Label>{FIELD_LABELS[cfg.parent.field] || "Parent"}</Label>
                  <Select value={form[cfg.parent.field] || ""} onValueChange={(v) => setForm({ ...form, [cfg.parent.field]: v })}>
                    <SelectTrigger data-testid="master-form-parent-select"><SelectValue placeholder="Select parent" /></SelectTrigger>
                    <SelectContent>
                      {(cfg.parent.field === "ward_id" && zoneFilter ? parents.filter((p) => p.zone_id === zoneFilter) : parents).map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p[cfg.parent.label_field]}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
            {cfg?.fields.map((f) => (
              <div key={f} className="space-y-1">
                {BOOL_FIELDS.includes(f) ? (
                  <div className="flex items-center justify-between border rounded-lg px-3 py-2">
                    <Label>{FIELD_LABELS[f] || f}</Label>
                    <Switch checked={!!form[f]} onCheckedChange={(v) => setForm({ ...form, [f]: v })} />
                  </div>
                ) : REF_FIELDS[f] ? (
                  <>
                    <Label>{FIELD_LABELS[f] || f}</Label>
                    <Select value={form[f] || ""} onValueChange={(v) => setForm({ ...form, [f]: v })}>
                      <SelectTrigger><SelectValue placeholder={`Select ${FIELD_LABELS[f]}`} /></SelectTrigger>
                      <SelectContent>
                        {(refOptions[REF_FIELDS[f]] || []).map((o) => (
                          <SelectItem key={o.id} value={o.id}>
                            {o.tax_head_name || o.fy_label || o.category_name || o.sub_category_name || o.usage_type_name || o.zone_name || o.const_type_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </>
                ) : (
                  <>
                    <Label>{FIELD_LABELS[f] || f}</Label>
                    <Input type={DATE_FIELDS.includes(f) ? "date" : NUM_FIELDS.includes(f) ? "number" : "text"}
                      step="any" value={form[f] ?? ""} onChange={(e) => setForm({ ...form, [f]: NUM_FIELDS.includes(f) ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value })} />
                  </>
                )}
              </div>
            ))}
            <Button type="submit" className="w-full" data-testid="master-form-submit-button">{editing ? "Save changes" : "Create"}</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
